// @file Shared composable for tracking photo album navigation state across different posts
import { Post } from '@@/types'
import { createSharedComposable } from '@vueuse/core'
import { ref } from 'vue'

export const usePhotoAlbumNavigationAcrossPosts = createSharedComposable(() => {
  const imageIndexByPostCid = ref<Record<Post['cid'], number>>({})
  const navigationCallbacks = ref<Record<Post['cid'], { previous: () => void; next: () => void } | undefined>>({})

  const getImageIndex = (postCid: Post['cid']): number => {
    return imageIndexByPostCid.value[postCid] ?? 0
  }

  const setImageIndex = (postCid: Post['cid'], index: number): void => {
    imageIndexByPostCid.value[postCid] = index
  }

  const registerNavigationCallbacks = (
    postCid: Post['cid'],
    callbacks: { previous: () => void; next: () => void },
  ): void => {
    navigationCallbacks.value[postCid] = callbacks
  }

  const unregisterNavigationCallbacks = (postCid: Post['cid']): void => {
    navigationCallbacks.value[postCid] = undefined
  }

  const navigateToPrevious = (postCid: Post['cid']): void => {
    setImageIndex(postCid, getImageIndex(postCid) - 1)
    navigationCallbacks.value[postCid]?.previous()
  }

  const navigateToNext = (postCid: Post['cid']): void => {
    setImageIndex(postCid, getImageIndex(postCid) + 1)
    navigationCallbacks.value[postCid]?.next()
  }

  return {
    getImageIndex,
    setImageIndex,
    registerNavigationCallbacks,
    unregisterNavigationCallbacks,
    navigateToPrevious,
    navigateToNext,
  }
})
